import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type UploadStatusTone = "progress" | "success" | "error";

const TONES: Record<UploadStatusTone, { box: string; icon: ReactNode }> = {
  progress: {
    box: "border-[#c9d6ea] bg-[#eef3fb] text-[#08254c]",
    icon: <Loader2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#2f5fa8]" />,
  },
  success: {
    box: "border-[#bfe3cc] bg-[#eaf7ef] text-[#17613a]",
    icon: <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[#27a155]" />,
  },
  error: {
    box: "border-red-200 bg-red-50 text-[#8f1f1f]",
    icon: <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[#d93636]" />,
  },
};

/**
 * Estado do envio no próprio painel: em andamento, concluído ou com erro. `details` vira uma
 * lista que começa fechada (uma linha por tabela, sem tomar a tela) e `onDismiss` põe o X.
 */
export function UploadStatusBanner({
  tone,
  message,
  details,
  onDismiss,
}: {
  tone: UploadStatusTone;
  message: string;
  details?: string[];
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { box, icon } = TONES[tone];
  const hasDetails = !!details && details.length > 0;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      data-tone={tone}
      className={`rounded-md border px-3.5 py-3 text-sm font-medium ${box}`}
    >
      <div className="flex items-start gap-2.5">
        {icon}
        <p className="m-0 min-w-0 flex-1 break-words">{message}</p>
        {hasDetails && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex shrink-0 items-center gap-1 rounded text-xs font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
          >
            {open ? t("Hide details") : t("Show details ({{count}})", { count: details!.length })}
            <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            aria-label={t("Dismiss")}
            onClick={onDismiss}
            className="shrink-0 rounded p-0.5 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        )}
      </div>
      {hasDetails && open && (
        <ul className="m-0 mt-2 max-h-48 list-disc space-y-1 overflow-y-auto pl-9 pr-1 text-xs font-normal">
          {details!.map((line) => (
            <li key={line} className="break-words">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
